import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { UmraPackagesService, ShortlistedPackage, ApprovalStatistics, UmraPassengerInfo } from '../services/umra-packages.service';
import {
  faHotel,
  faListSquares,
  faCheckCircle,
  faFile,
  faCalendar,
  faArrowRightRotate,
  faPlane,
  faPlaneDeparture,
  faLocationDot,
  faLocationArrow,
  faEye,
  faKaaba,
  faRoute,
  faPlusCircle,
  faFileCirclePlus,
  faExclamationTriangle,
  faCopy,
  faSearch,
  faMap,
  faXmarkCircle,
  faSquareArrowUpRight,
  faInfoCircle,
  faRupeeSign,
  faFileLines,
  faTable,
  faListCheck,
  faClock,
  faInbox
} from '@fortawesome/free-solid-svg-icons';
interface ApprovalCounts {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
}
@Component({
  selector: 'app-umra-manager-approval-list',
  templateUrl: './umra-manager-approval-list.component.html',
  styleUrls: ['./umra-manager-approval-list.component.css']
})
export class UmraManagerApprovalListComponent implements OnInit, OnDestroy {

  faHotel = faHotel;
  faDiamond = faListSquares;
  faCheckCircle = faCheckCircle;
  faExclamation = faExclamationTriangle;
  faCalendar = faCalendar;
  faClock = faArrowRightRotate;
  faPlane = faPlane;
  faPlaneDeparture = faPlaneDeparture;
  faLocationDot = faLocationDot;
  faLocationArrow = faLocationArrow;
  faEye = faEye;
  faKaaba = faKaaba;
  faRoute = faRoute;
  faPlusCircle = faPlusCircle;
  faFileCirclePlus = faFileCirclePlus;
  faFile = faFile;
  faCopy = faCopy;
  faSearch = faSearch;
  faMarker = faMap;
  faXCircle = faXmarkCircle;
  faSquareArrowUpRight = faSquareArrowUpRight;
  faInfoCircle = faInfoCircle;
  faRupeeSign = faRupeeSign;
  faFileLines = faFileLines;
  faTable = faTable;
  faListCheck = faListCheck;
  faPending = faClock;
  faInbox = faInbox;
  // Data
  allPackages: ShortlistedPackage[] = [];
  filteredPackages: ShortlistedPackage[] = [];
  statistics: ApprovalStatistics | null = null;

  // Filters
  searchControl = new FormControl('');
  selectedStatus: string = 'all';
  statusCounts: ApprovalCounts = { total: 0, pending: 0, approved: 0, rejected: 0 };

  // UI State
  loading = false;
  error: string | null = null;

  // Modal
  showPassengersModal = false;
  showRejectModal = false;
  showApproveModal = false;
  selectedPackage: ShortlistedPackage | null = null;
  rejectionReason = '';
  approvalRemarks = '';
  hvNumber = "";

  // Confirmation
  processingAction = false;
  successMessage: string | null = null;

  private destroy$ = new Subject<void>();
  constructor(private approvalService: UmraPackagesService, private router: Router) { }

  ngOnInit(): void {
    this.loadApprovals();
    this.setupSearchListener();
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSearchListener(): void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.applyFilters();
      });
  }

  loadApprovals(): void {
    this.loading = true;
    this.error = null;

    this.approvalService.getPendingApprovals().subscribe({
      next: (response) => {
        if (response.success) {
          this.allPackages = response.data;
          this.statusCounts = {
            total: response.totalCount,
            pending: response.pendingCount,
            approved: response.approvedCount,
            rejected: response.rejectedCount
          };
          this.applyFilters();
          this.loadStatistics();
        } else {
          this.error = 'Failed to load approvals';
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading approvals:', error);
        this.error = 'Failed to load approvals. Please try again.';
        this.loading = false;
      }
    });
  }

  loadStatistics(): void {
    this.approvalService.getApprovalStatistics().subscribe({
      next: (response) => {
        if (response.success) {
          this.statistics = response.statistics;
        }
      },
      error: (error) => {
        console.error('Error loading statistics:', error);
      }
    });
  }
  loadCustomAddition():void {
    this.router.navigate(['/add-new-umra-offer']);
  }

  filterByStatus(status: string): void {
    this.selectedStatus = status;
    this.applyFilters();
  }

  applyFilters(): void {
    let result = [...this.allPackages];

    // Filter by status
    if (this.selectedStatus !== 'all') {
      result = result.filter(pkg =>
        pkg.shortlistStatus.toLowerCase() === this.selectedStatus.toLowerCase()
      );
    }

    // Search filter
    const searchTerm = (this.searchControl.value || '').toLowerCase().trim();
    if (searchTerm) {
      result = result.filter(pkg =>
        (pkg.customerName.toLowerCase().includes(searchTerm)) ||
        (pkg.packageTitle.toLowerCase().includes(searchTerm)) ||
        (pkg.packageCode.toLowerCase().includes(searchTerm)) ||
        (pkg.email.toLowerCase().includes(searchTerm))
      );
    }

    this.filteredPackages = result;
  }
  passengersIncluded: UmraPassengerInfo[] = [];
  //Passengers Modal
  openPassengersModal(pkg: ShortlistedPackage): void {
    this.passengersIncluded = pkg.passengers;
    this.showPassengersModal = true;
  }
  // APPROVE PACKAGE
  openApproveModal(pkg: ShortlistedPackage): void {
    this.selectedPackage = pkg;
    this.approvalRemarks = '';
    this.hvNumber = '';
    this.showApproveModal = true;
  }

  closeApproveModal(): void {
    this.showApproveModal = false;
    this.selectedPackage = null;
    this.approvalRemarks = '';
    this.hvNumber = '';
  }

  approvePackage(): void {
    if (!this.selectedPackage) return;
    if (!this.hvNumber.trim()) { this.error = 'HV Number is required for approval'; return; }
    this.processingAction = true;

    const request = {
      remarks: this.approvalRemarks,
      hvNumber: this.hvNumber
    };

    this.approvalService.approvePackage(this.selectedPackage.id, request).subscribe({
      next: (response) => {
        if (response.success) {
          this.successMessage = `Package approved successfully!`;
          this.closeApproveModal();
          this.loadApprovals();
          setTimeout(() => this.successMessage = null, 3000);
        } else {
          this.error = response.message || 'Failed to approve package';
        }
        this.processingAction = false;
      },
      error: (error) => {
        console.error('Error approving package:', error);
        this.error = 'Failed to approve package. Please try again.';
        this.processingAction = false;
      }
    });
  }

  // REJECT PACKAGE
  openRejectModal(pkg: ShortlistedPackage): void {
    this.selectedPackage = pkg;
    this.rejectionReason = '';
    this.showRejectModal = true;
  }

  closeRejectModal(): void {
    this.showRejectModal = false;
    this.selectedPackage = null;
    this.rejectionReason = '';
  }

  rejectPackage(): void {
    if (!this.selectedPackage || !this.rejectionReason.trim()) { this.error = 'Remarks are required for rejection'; return; };

    this.processingAction = true;

    const request = {
      rejectionReason: this.rejectionReason
    };

    this.approvalService.rejectPackage(this.selectedPackage.id, request).subscribe({
      next: (response) => {
        if (response.success) {
          this.successMessage = `Package rejected successfully!`;
          this.closeRejectModal();
          this.loadApprovals();
          setTimeout(() => this.successMessage = null, 3000);
        } else {
          this.error = response.message || 'Failed to reject package';
        }
        this.processingAction = false;
      },
      error: (error) => {
        console.error('Error rejecting package:', error);
        this.error = 'Failed to reject package. Please try again.';
        this.processingAction = false;
      }
    });
  }
  loadPackageManagement(): void {
    this.router.navigate(['/package-management']);
  }
  loadHotelManagement(): void {
    this.router.navigate(['/hotel-management']);
  }
  loadSuppliersManagement(): void {
    this.router.navigate(['/umra-supplier-management']);
  }
  // HELPERS
  getStatusBadgeClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'pending': return 'bg-warning text-dark';
      case 'approved': return 'bg-success';
      case 'rejected': return 'bg-danger';
      default: return 'bg-secondary';
    }
  }

  formatPrice(price: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  }

  formatDate(date: string | Date): string {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getActiveFilterClass(status: string): string {
    return this.selectedStatus === status ? 'active' : '';
  }
}
